import { zodResolver } from '@hookform/resolvers/zod';
import { endOfDay, format, startOfDay } from 'date-fns';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { z } from 'zod';

import { AudienceSelect } from '@/components/AudienceSelect';
import { FormGroup } from '@/components/FormGroup';
import { Button } from '@/components/ui/Button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/Dialog';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select';
import { useCreateMerchandisingCollectionSlice } from '@/hooks/useCreateMerchandisingCollectionSlice';
import i18n from '@/lib/i18n';
import { sliceInputText } from '@/pages/MerchandisingCollections/utils/sliceInputMap';
import { SliceInputType } from '@/services/MerchandisingCollectionsService';

const formSchema = z
  .object({
    name: z.string().min(1, {
      message: i18n.t(
        'pages.MerchandisingCollections.pages.Collection.components.SlicesList.components.CreateSliceModal.form.nameError',
      ),
    }),
    inputType: z.nativeEnum(SliceInputType).default(SliceInputType.DEFAULT),
    inputValue: z.string().optional(),
    startsAt: z.string().min(1, {
      message: i18n.t(
        'pages.MerchandisingCollections.pages.Collection.components.SlicesList.components.CreateSliceModal.form.startsAtError1',
      ),
    }),
    endsAt: z.string().min(1, {
      message: i18n.t(
        'pages.MerchandisingCollections.pages.Collection.components.SlicesList.components.CreateSliceModal.form.endsAtError1',
      ),
    }),
  })
  .superRefine((data, ctx) => {
    const start = new Date(data.startsAt);
    const end = new Date(data.endsAt);

    if (start > end) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t(
          'pages.MerchandisingCollections.pages.Collection.components.SlicesList.components.CreateSliceModal.form.startsAtError2',
        ),
        path: ['startsAt'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t(
          'pages.MerchandisingCollections.pages.Collection.components.SlicesList.components.CreateSliceModal.form.endsAtError2',
        ),
        path: ['endsAt'],
      });
    }

    if (data.inputType !== SliceInputType.DEFAULT && !data.inputValue?.trim()) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: i18n.t(
          'pages.MerchandisingCollections.pages.Collection.components.SlicesList.components.CreateSliceModal.form.inputValueError',
        ),
        path: ['inputValue'],
      });
    }
  });

type FormData = z.infer<typeof formSchema>;

interface CreateSliceModalProps {
  collectionId: number;
  open: boolean;
  onClose: () => void;
}

export function CreateSliceModal({
  collectionId,
  open,
  onClose,
}: CreateSliceModalProps) {
  const { t } = useTranslation('translation', {
    keyPrefix:
      'pages.MerchandisingCollections.pages.Collection.components.SlicesList.components.CreateSliceModal',
  });

  const {
    formState: { isSubmitting, errors },
    control,
    watch,
    register,
    handleSubmit,
    reset,
  } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      inputType: SliceInputType.DEFAULT,
      startsAt: format(startOfDay(new Date()), "yyyy-MM-dd'T'HH:mm"),
      endsAt: format(endOfDay(new Date()), "yyyy-MM-dd'T'HH:mm"),
    },
  });

  const inputType = watch('inputType');

  const { createMerchandisingCollectionSlice } =
    useCreateMerchandisingCollectionSlice();

  const onSubmit = handleSubmit(async (data) => {
    try {
      await createMerchandisingCollectionSlice({
        collectionId,
        dto: {
          name: data.name,
          inputType: data.inputType,
          inputValue: data.inputValue ?? null,
          startsAt: new Date(data.startsAt),
          endsAt: new Date(data.endsAt),
        },
      });

      toast.success(t('toast.success'));
      onClose();
      reset();
    } catch {
      toast.error(t('toast.error'));
    }
  });

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{t('title')}</DialogTitle>
          <DialogDescription>{t('description')}</DialogDescription>
        </DialogHeader>

        <form className="space-y-6" onSubmit={onSubmit}>
          <FormGroup error={{ message: errors.name?.message, font: 'sm' }}>
            <Label htmlFor="slice-name">{t('form.nameLabel')}</Label>

            <Input
              id="slice-name"
              placeholder={t('form.namePlaceholder')}
              {...register('name')}
            />
          </FormGroup>

          <FormGroup error={{ message: errors.inputType?.message, font: 'sm' }}>
            <Controller
              name="inputType"
              control={control}
              render={({ field }) => (
                <Select onValueChange={field.onChange}>
                  <SelectTrigger>
                    <SelectValue placeholder={inputType} />
                  </SelectTrigger>

                  <SelectContent>
                    {Object.values(SliceInputType).map((value) => (
                      <SelectItem
                        key={value}
                        value={value}
                        disabled={
                          value === SliceInputType.AI ||
                          value === SliceInputType.CSV
                        }
                      >
                        {sliceInputText(value)}{' '}
                        {(value === SliceInputType.AI ||
                          value === SliceInputType.CSV) &&
                          `(${t('form.comingSoonText')})`}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            />
          </FormGroup>

          {inputType === SliceInputType.AUDIENCE && (
            <FormGroup
              error={{ message: errors.inputValue?.message, font: 'sm' }}
            >
              <Controller
                name="inputValue"
                control={control}
                render={({ field }) => (
                  <AudienceSelect onValueChange={field.onChange} />
                )}
              />
            </FormGroup>
          )}

          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <FormGroup
              error={{ message: errors.startsAt?.message, font: 'sm' }}
            >
              <Label htmlFor="starts-at">{t('form.startsAtLabel')}</Label>

              <Input
                id="starts-at"
                type="datetime-local"
                {...register('startsAt')}
              />
            </FormGroup>

            <FormGroup error={{ message: errors.endsAt?.message, font: 'sm' }}>
              <Label htmlFor="ends-at">{t('form.endsAtLabel')}</Label>

              <Input
                id="ends-at"
                type="datetime-local"
                {...register('endsAt')}
              />
            </FormGroup>
          </div>

          <div className="flex justify-end space-x-2">
            <Button type="button" variant="outline" onClick={onClose}>
              {t('cancelButtonLabel')}
            </Button>

            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting
                ? t('form.submitButtonLabelLoading')
                : t('form.submitButtonLabel')}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
}
